import { useMemo, useState } from 'react'
import { withStory, WithStoryProps } from '@storyofams/storyblok-toolkit'
import { flatten, uniqBy, includes } from 'lodash'
import { GetStaticProps, NextPage } from 'next'
import { defineMessages, useIntl } from 'react-intl'

import { Seo, Container, Header } from '~components'
import { enhancedStaticProps, withLayout } from '~lib'
import { ArticleCard, ArticlesGrid, Tabs } from '~components/blog'
import { BlogpageItems } from '~lib/storyblok/sdk'

const messages = defineMessages({
  allItems: 'All items',
})

interface BlogOverviewProps extends WithStoryProps {
  blogPages: BlogpageItems
}

const BlogOverview: NextPage<BlogOverviewProps> = ({ story, blogPages }) => {
  const intl = useIntl()
  const [category, setCategory] = useState(null)

  const allArticles = useMemo(
    () => blogPages?.items?.filter(({ slug }) => slug !== story?.slug),
    [blogPages],
  )

  const categories = useMemo(
    () =>
      uniqBy(
        flatten(allArticles?.map(({ content }) => content?.categories)).map(
          ({ content }) => ({ title: content.title, _uid: content._uid }),
        ),
        '_uid',
      ),
    [allArticles],
  )

  const filteredArticles = useMemo(
    () =>
      !category
        ? allArticles
        : allArticles?.filter(({ content }) =>
            includes(
              content?.categories?.map(({ content }) => content?._uid),
              category,
            ),
          ),
    [category],
  )

  return (
    <>
      <Seo story={story} />

      <Header
        title={story?.content?.title}
        img={story?.content?.bg}
        justifyContent={['center', 'center']}
        alignItems="center"
        textProps={{
          justifyContent: ['center', 'center'],
          maxWidth: ['100%', '100%'],
        }}
      />

      <Container bg="grey200">
        <ArticleCard {...story?.content?.featuredItem} isFeatured />

        <Tabs
          items={[
            { title: intl.formatMessage(messages.allItems), _uid: null },
            ...categories,
          ]}
          activeTab={category}
          setActiveTab={setCategory}
        />

        <ArticlesGrid articles={filteredArticles} category={category} />
      </Container>
    </>
  )
}

export default withLayout(withStory(BlogOverview))

export const getStaticProps: GetStaticProps<WithStoryProps> =
  enhancedStaticProps(async ({ sdk, defaultLocale, locale }) => {
    let story = null
    let blogPages = null
    let notFound = false

    try {
      story = (
        await sdk.blogOverviewPage({
          id: `${defaultLocale !== locale ? `${locale}/` : ''}blog/`,
          lang: defaultLocale !== locale ? locale : 'default',
        })
      ).BlogoverviewItem
      blogPages = (
        await sdk.blogPages({
          prefix: `${defaultLocale !== locale ? `${locale}/*` : ''}`,
        })
      ).BlogpageItems
    } catch (e) {
      notFound = true
    }

    if (notFound || !story || !blogPages) {
      return {
        props: {
          story: {},
        },
        notFound: true,
        revalidate:
          process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
      }
    }

    return {
      props: {
        story,
        blogPages,
      },
      notFound: notFound || !story,
      revalidate:
        process.env.NEXT_PUBLIC_ENVIRONMENT === 'development' ? 1 : 60,
    }
  })
